/**
 * InboxRoleSwitcher — pill toggle for users who own MORE THAN ONE
 * messaging surface across:
 *   • 'roster'  → Agent / Brand roster messages   → /roster/messages
 *   • 'client'  → Client Portal (brand chat)      → /messages
 *   • 'athlete' → Athlete dashboard messages      → /athlete/messages
 *
 * Rendered on /profile. Silent-null if the user only has one (or zero)
 * inbox surface.
 *
 * Persistence: localStorage `dl_active_inbox_<user_id>` =
 *   'roster' | 'client' | 'athlete'.
 * Change is broadcast via `inbox-role:changed` window event so
 * BottomNav re-renders instantly without a page reload.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Building2, Award } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const INBOX_ROLE_KEY = (uid) => `dl_active_inbox_${uid}`;
export const INBOX_ROLE_EVENT = 'inbox-role:changed';

export const readInboxRole = (uid) => {
  if (!uid) return null;
  try { return localStorage.getItem(INBOX_ROLE_KEY(uid)) || null; } catch { return null; }
};

const InboxRoleSwitcher = () => {
  const { user } = useUserAuth();
  const [portals, setPortals] = useState(null);   // null = loading, [] = empty
  const [active, setActive] = useState(null);

  const isClientWithBrand = !!user?.client_portal;

  useEffect(() => {
    if (!user) { setPortals(null); return undefined; }
    let cancelled = false;
    axios.get(`${API}/me/portals`, { withCredentials: true })
      .then(r => { if (!cancelled) setPortals(r.data.portals || []); })
      .catch(() => { if (!cancelled) setPortals([]); });
    return () => { cancelled = true; };
  }, [user?.user_id]);

  useEffect(() => {
    if (!user) return;
    setActive(readInboxRole(user.user_id) || 'roster');
  }, [user?.user_id]);

  if (!user || portals === null) return null;

  const rosterOwner = portals.find(p => p.kind === 'agent' || p.kind === 'brand') || null;
  const athletePortal = portals.find(p => p.kind === 'athlete') || null;

  // Build the list of available inbox pills in priority order.
  const options = [];
  if (rosterOwner) {
    options.push({
      key: 'roster',
      icon: Users,
      label: rosterOwner.kind === 'agent' ? 'Agent Roster' : 'Brand Roster',
    });
  }
  if (isClientWithBrand) {
    const brandName = user.client_portal?.brand_name || 'Client';
    options.push({
      key: 'client',
      icon: Building2,
      label: `${brandName} Portal`,
    });
  }
  if (athletePortal) {
    options.push({
      key: 'athlete',
      icon: Award,
      label: 'Athlete Dashboard',
    });
  }

  // Need at least two inbox surfaces to justify the switch.
  if (options.length < 2) return null;

  const pick = (choice) => {
    setActive(choice);
    try { localStorage.setItem(INBOX_ROLE_KEY(user.user_id), choice); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent(INBOX_ROLE_EVENT, { detail: { choice } })); } catch { /* ignore */ }
  };

  const pillBase = 'flex-1 flex items-center justify-center gap-2 rounded-full px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-bold transition min-w-0';
  const pillOn = 'bg-white text-black shadow-[0_4px_18px_-4px_rgba(255,255,255,0.35)]';
  const pillOff = 'bg-white/[0.06] text-white/60 hover:text-white/90 hover:bg-white/[0.1]';

  return (
    <section
      className="max-w-5xl mx-auto px-4 sm:px-6 mt-1 mb-4"
      data-testid="inbox-role-switcher"
    >
      <p className="text-[10px] uppercase tracking-[0.35em] text-white/40 font-bold mb-2">
        Active messages inbox
      </p>
      <div
        className="flex gap-1.5 p-1 rounded-full border border-white/10 bg-white/[0.03] overflow-hidden"
        role="tablist"
        aria-label="Choose which inbox opens from the bottom nav"
      >
        {options.map(opt => {
          const Icon = opt.icon;
          const isOn = active === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              role="tab"
              aria-selected={isOn}
              onClick={() => pick(opt.key)}
              className={`${pillBase} ${isOn ? pillOn : pillOff}`}
              data-testid={`inbox-role-pill-${opt.key}`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{opt.label}</span>
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-white/35 mt-2 leading-relaxed">
        Sets which inbox opens when you tap the Messages icon in the bottom bar.
      </p>
    </section>
  );
};

export default InboxRoleSwitcher;
